import React, { useEffect, useMemo, useState, useContext } from 'react';

import { CompleteVehicleEntry, Picture, useServicesContext } from '@oneaudi/vtp-shared';

import SwipePicture from './swipePicture/SwipePicture';
import {
  filterImages,
  getFilteredPictureByGroup,
  PICTURE_TYPE_PHOTO,
  PICTURE_TYPE_VTP,
} from '../utils/imagesFilter';

import { StageContainer } from '../styles/Stage.styles';
import { AvpWebstreamWrapper } from './AvpWebstreamIntegration/AvpWebstreamWrapper';
import { StageContext } from '../hooks/StageContext';
import { ContentService } from '@oneaudi/content-service';
import { ContentFields } from '../../../types/editor';

interface Props {
  vehicleRaw: CompleteVehicleEntry;
}

export const sortPictures = (images: Picture[]): Picture[] => {
  return images.sort((current, next) => {
    if (current.type === 'render' && next.type === 'photo') {
      return -1;
    }
    if (current.type === 'photo' && next.type === 'render') {
      return 1;
    }
    return 0;
  });
};
const StageWrapper: React.FC<Props> = ({ vehicleRaw }) => {
  const { hideRenderImages } = vehicleRaw.basic;

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window?.innerWidth : 400
  );

  // Attach event listener on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window?.addEventListener('resize', handleResize);
    }

    // Clean up the event listener on component unmount
    return () => {
      if (typeof window !== 'undefined') {
        window?.removeEventListener('resize', handleResize);
      }
    };
  }, [windowWidth]);

  const fallbackImage = useMemo(() => {
    return vehicleRaw.basic?.fallbackPictures?.filter((picture: Picture) =>
      picture.key.startsWith('render_')
    );
  }, [vehicleRaw.basic?.fallbackPictures]);

  const setFilteredImages = useMemo(() => {
    return {
      renderImages:
        vehicleRaw.detail?.pictures?.filter((picture: Picture) =>
          picture.key.startsWith(PICTURE_TYPE_VTP)
        ) || [],
      dealerPhotoImages: vehicleRaw.basic?.pictures?.filter((picture: Picture) =>
        picture.type?.startsWith(PICTURE_TYPE_PHOTO)
      ),
    };
  }, [vehicleRaw.detail?.pictures]);

  const { renderImages, dealerPhotoImages } = setFilteredImages;

  const isNewCarStage = !!vehicleRaw?.basic?.type && vehicleRaw?.basic?.type.toLowerCase() === 'n';

  const filteredImages = filterImages(
    dealerPhotoImages,
    renderImages,
    fallbackImage,
    hideRenderImages!,
    isNewCarStage
  );

  // Update window width on window resize
  const handleResize = () => {
    setWindowWidth(typeof window !== 'undefined' ? window?.innerWidth : 400);
  };

  const [pictures] = getFilteredPictureByGroup(isNewCarStage, windowWidth, filteredImages);

  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const contentFields: ContentFields = audiContentService.getContent().fields;
  // const avp3dWebstreaming = contentFields?.avp3dWebstreaming;

  const { webstreamLayerOpen } = useContext(StageContext);

  return (
    <div data-testid="stage-wrapper" style={wrapperStyle}>
      {webstreamLayerOpen && (
        <AvpWebstreamWrapper
          configId={contentFields?.configId}
          prString={vehicleRaw?.basic?.avpString}
        />
      )}
      <StageContainer>
        <SwipePicture data-test-id="Container" pictures={pictures} vehicle={vehicleRaw.basic} />
      </StageContainer>
    </div>
  );
};

const wrapperStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  position: 'relative',
};

export default StageWrapper;
